'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ConfirmDialog,
  InquiryFlow,
  type FlowDocument,
  type NavigationRequest,
} from '@miso/design-system';
import type { VisitCounselView } from '@/entities/visit-counsel/model/visit-counsel.types';
import {
  SERVICE_SET_VALUES,
  type MovingItemRequest,
  type ServiceSet,
} from '@/entities/visit-counsel/model/room-observing.types';
import type { AdditionalSpaceObservingSession } from '@/entities/visit-counsel/model/visit-counsel.types';
import { MovingItemsFlowAdapter } from '@/features/visit-counsel/moving-items-flow';
import type { RoomTypeValue } from '@/entities/visit-counsel/model/house-sketch.types';
import {
  roomTypeFromSketchRoom,
  roomTypeFromSpaceType,
} from '@/features/visit-counsel/moving-items-flow/model/resolveObservingRoomType';
import {
  EditObservingFlow,
  usePendingObservingSessionIds,
} from '@/features/visit-counsel/room-observing-flow';
import {
  additionalSpaceEntriesToDoneInstances,
  allAdditionalSpaceEntriesDone,
  buildAdditionalSpacePickerEntries,
  type AdditionalSpacePickerEntry,
} from '../model/buildAdditionalSpacePickerEntries';

// ── Constants ────────────────────────────────────────────────────────────────

const PICK_QUESTION_ID = 'additionalSpaceToRecord';
const PICK_STEP_ID = 'pick';
const TERMINAL_ID = 'additional-space-record-done';
const ROOT_FLOW_ID = 'additional-space-pick-flow';

// ── 헬퍼: entry → MovingItemsFlowAdapter target ────────────────────────────────

interface SelectedTarget {
  sessionId: string;
  isFirstStart: boolean;
  roomLabel: string;
  roomType?: RoomTypeValue;
  initialMovingItems: MovingItemRequest[] | undefined;
  canRecordMovingItems: boolean;
}

function toMovingItemRequests(
  session: AdditionalSpaceObservingSession
): MovingItemRequest[] | undefined {
  const items = session.movingItems;
  if (!items || items.length === 0) return undefined;
  const mapped = items
    .filter((it) => SERVICE_SET_VALUES.includes(it.serviceSet as ServiceSet))
    .map((it) => ({
      specId: it.specId,
      attributes: it.attributes ?? {},
      serviceSet: it.serviceSet as ServiceSet,
    }));
  return mapped.length > 0 ? mapped : undefined;
}

function entryToTarget(entry: AdditionalSpacePickerEntry): SelectedTarget {
  return {
    sessionId: entry.sessionId,
    // 사용자가 picker 카드를 다시 클릭하면 본 어댑터의 `MovingItemsFlowAdapter` 가 새로 마운트된다.
    // 미완료(REQUESTED) → 첫 시작, 그 외 → record API 로 진행.
    isFirstStart: entry.isFirstStart,
    roomLabel: entry.title,
    roomType: entry.linkedSketchRoom
      ? roomTypeFromSketchRoom(entry.linkedSketchRoom)
      : roomTypeFromSpaceType(entry.session.spaceType),
    initialMovingItems: toMovingItemRequests(entry.session),
    canRecordMovingItems: entry.canRecordMovingItems,
  };
}

// ── FlowDocument 빌더 ────────────────────────────────────────────────────────

interface BuildFlowOptions {
  entries: AdditionalSpacePickerEntry[];
  pendingSessionIds: ReadonlySet<string>;
}

function buildPickFlow({ entries, pendingSessionIds }: BuildFlowOptions): FlowDocument {
  return {
    schemaVersion: 1,
    id: ROOT_FLOW_ID,
    initial: PICK_STEP_ID,
    steps: {
      [PICK_STEP_ID]: {
        kind: 'questions',
        questions: [
          {
            id: PICK_QUESTION_ID,
            layout: 'L2',
            title: '기록할 추가공간을 선택해주세요.',
            input: {
              kind: 'item-picker',
              required: false,
              multiple: false,
              mode: 'navigation',
              requireAllItems: true,
              reviewTitle: '기록할 추가공간',
              reviewButtonLabel: '완료',
              sections: [
                {
                  id: 'additional-space',
                  title: '추가공간',
                  items: entries.map((e) => ({
                    id: e.itemId,
                    title: e.title,
                    icon: e.iconEmoji,
                    ...(e.description ? { description: e.description } : {}),
                    ...(pendingSessionIds.has(e.sessionId) ? { pending: true as const } : {}),
                  })),
                },
              ],
            },
          },
        ],
        next: { kind: 'terminal', target: TERMINAL_ID },
      },
    },
    terminals: {
      [TERMINAL_ID]: { kind: 'placeholder-auto' },
    },
  };
}

// ── Adapter ─────────────────────────────────────────────────────────────────

export interface AdditionalSpaceFlowAdapterProps {
  visitCounselId: string;
  counsel: VisitCounselView;
  showToast: (message: string, severity?: 'success' | 'error' | 'warning' | 'neutral') => void;
  refetch: () => Promise<{ counsel?: VisitCounselView | null } | undefined>;
  /** 모든 추가공간이 완료되면 호출 — page 가 stage 재평가로 done 단계 진입. */
  onAfterDone: () => void;
  /** 사용자 취소(뒤로가기) — page 가 fallback. */
  onCancel: () => void;
}

/**
 * 추가공간 촬영 stage 어댑터.
 *
 * 방 어댑터(`RoomObservingFlowAdapter`) 와 동일한 picker UX 로 통일:
 *
 *   1. counsel 스냅샷 → `buildAdditionalSpacePickerEntries` 로 공식 추가공간(완료 + 미완료)
 *      + edge-case 비공식 미완료 항목을 entry 배열로 만든다.
 *   2. `InquiryFlow` + item-picker(navigation 모드) 로 카드 그리드 표시. 완료 항목은
 *      `additionalSpaceEntriesToDoneInstances` 로 done badge prefill.
 *   3. 카드 클릭 시:
 *        - 미완료 → 즉시 nested `MovingItemsFlowAdapter` 진입.
 *        - 완료   → "이미 촬영한 추가공간이에요" 확인 모달 → 확인 시 `EditObservingFlow`
 *          영상만(editTarget='video') 모드로 직행. (방 선택과 달리 추가공간은 영상만 재촬영.)
 *   4. nested 어댑터 `onAfterDone` → setSelected(null) → counsel refetch 로 entries 재계산.
 *      모든 entry 가 완료되면 useEffect 가 `onAfterDone` 을 호출해 page stage 가
 *      `done` 으로 전환.
 *
 * 이전 동작과의 차이:
 *   - pending 1 개여도 자동 진입하지 않는다. 사용자가 명시적으로 선택해야 진입.
 *   - 완료된 추가공간도 picker 에 노출되고 편집 가능.
 *   - 백그라운드 폴링 중인 세션은 picker 카드가 dimmed + spinner.
 */
export function AdditionalSpaceFlowAdapter({
  visitCounselId,
  counsel,
  showToast,
  refetch,
  onAfterDone,
  onCancel,
}: AdditionalSpaceFlowAdapterProps) {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [selected, setSelected] = useState<SelectedTarget | null>(null);
  const [editConfirmEntry, setEditConfirmEntry] = useState<AdditionalSpacePickerEntry | null>(null);
  /** ConfirmDialog 확인 후 영상만 편집으로 직행하는 entry */
  const [editingEntry, setEditingEntry] = useState<AdditionalSpacePickerEntry | null>(null);

  const entries = useMemo(() => buildAdditionalSpacePickerEntries(counsel), [counsel]);

  const pendingSessionIds = usePendingObservingSessionIds();

  const allDone = entries.length > 0 && allAdditionalSpaceEntriesDone(entries);

  /**
   * 모든 entry 가 완료되면 onAfterDone 으로 전환 — page 가 stage 를 done 으로 재평가.
   * nested 어댑터가 종료되어 selected===null 인 시점에만 실행한다.
   * entries 가 0개일 때도 즉시 호출(stage 진입 직후 추가공간이 사라진 케이스).
   */
  const fireDoneRef = useRef(false);
  useEffect(() => {
    if (selected) return;
    if (entries.length > 0 && !allDone) {
      fireDoneRef.current = false;
      return;
    }
    if (fireDoneRef.current) return;
    fireDoneRef.current = true;
    onAfterDone();
  }, [selected, entries.length, allDone, onAfterDone]);

  const flow = useMemo(
    () => buildPickFlow({ entries, pendingSessionIds }),
    [entries, pendingSessionIds]
  );

  const initialValues = useMemo(
    () => ({ [PICK_QUESTION_ID]: additionalSpaceEntriesToDoneInstances(entries) }),
    [entries]
  );

  /**
   * InquiryFlow 의 reducer 는 lazy initializer 라 첫 마운트에만 `initialValues` 를
   * 적용한다. 폴링이 끝나 새 done entry 가 생기면 ItemPicker 의 ✓ badge 가 즉시
   * 반영되지 않으므로, done 집합 시그니처가 바뀔 때만 key 로 강제 remount.
   */
  const doneSignature = useMemo(() => {
    const ids: string[] = [];
    for (const e of entries) if (e.done) ids.push(e.itemId);
    ids.sort();
    return ids.join('|');
  }, [entries]);

  const entryById = useMemo(() => {
    const map = new Map<string, AdditionalSpacePickerEntry>();
    for (const e of entries) map.set(e.itemId, e);
    return map;
  }, [entries]);

  const enterTargetForEntry = useCallback((entry: AdditionalSpacePickerEntry) => {
    setSelected(entryToTarget(entry));
  }, []);

  const handleNavigation = useCallback(
    ({ item }: NavigationRequest) => {
      const entry = entryById.get(item.id);
      if (!entry) return;
      if (entry.done) {
        setEditConfirmEntry(entry);
        return;
      }
      enterTargetForEntry(entry);
    },
    [entryById, enterTargetForEntry]
  );

  const handleEditConfirm = useCallback(() => {
    if (!editConfirmEntry) return;
    const entry = editConfirmEntry;
    setEditConfirmEntry(null);
    // 추가공간은 영상만 편집 — EditTargetSelector 없이 바로 video 모드로 진입.
    setEditingEntry(entry);
  }, [editConfirmEntry]);

  const handleEditCancel = useCallback(() => {
    setEditConfirmEntry(null);
  }, []);

  // ── nested adapter callbacks ─────────────────────────────────────────────
  const handleNestedAfterDone = useCallback(async (_nextCounsel: VisitCounselView) => {
    setSelected(null);
  }, []);

  const handleNestedCancel = useCallback(() => {
    setSelected(null);
  }, []);

  // ── 렌더 ──────────────────────────────────────────────────────────────────
  if (selected) {
    return (
      <MovingItemsFlowAdapter
        key={selected.sessionId}
        visitCounselId={visitCounselId}
        sessionId={selected.sessionId}
        isAdditionalSpaceMoving={true}
        isFirstStart={selected.isFirstStart}
        roomLabel={selected.roomLabel}
        roomType={selected.roomType}
        initialMovingItems={selected.initialMovingItems}
        canRecordMovingItems={selected.canRecordMovingItems}
        showToast={showToast}
        refetch={refetch}
        onAfterDone={handleNestedAfterDone}
        onCancel={handleNestedCancel}
      />
    );
  }

  if (editingEntry) {
    const editMoveTarget = entryToTarget(editingEntry);
    return (
      <EditObservingFlow
        key={`${editMoveTarget.sessionId}-video`}
        visitCounselId={visitCounselId}
        sessionId={editMoveTarget.sessionId}
        isAdditionalSpaceMoving={true}
        roomLabel={editMoveTarget.roomLabel}
        roomType={editMoveTarget.roomType}
        initialMovingItems={editMoveTarget.initialMovingItems}
        editTarget="video"
        canRecordMovingItems={editMoveTarget.canRecordMovingItems}
        showToast={showToast}
        refetch={refetch}
        onAfterDone={(counsel) => {
          setEditingEntry(null);
          handleNestedAfterDone(counsel);
        }}
        onBack={() => setEditingEntry(null)}
        container={wrapperRef.current}
      />
    );
  }

  // entries 가 0개이거나 모두 완료된 직후 — useEffect 가 onAfterDone 을 호출하기 전 placeholder.
  if (entries.length === 0 || allDone) {
    return (
      <div ref={wrapperRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }} />
    );
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <InquiryFlow
        key={doneSignature}
        flow={flow}
        initialValues={initialValues}
        registries={{ terminals: {} }}
        onComplete={() => {
          // navigation 모드 + requireAllItems=true 의 "완료" CTA — 모든 카드가 done 상태가 된
          // 시점에 호출된다. allDone 이 true 이므로 onAfterDone 으로 전환.
          onAfterDone();
        }}
        onCancel={onCancel}
        onError={(err) => {
          showToast(err.message ?? '진행 오류가 발생했습니다', 'error');
          onCancel();
        }}
        container={wrapperRef.current}
        onRequestNavigation={handleNavigation}
      />

      <ConfirmDialog
        open={!!editConfirmEntry}
        title="이미 촬영한 추가공간이에요"
        description={`${editConfirmEntry?.title ?? ''}은(는) 이미 촬영이 완료된 공간이에요.\n영상을 다시 촬영하시겠어요?`}
        confirmLabel="영상 재촬영"
        onConfirm={handleEditConfirm}
        onCancel={handleEditCancel}
        container={wrapperRef.current}
      />
    </div>
  );
}

export default AdditionalSpaceFlowAdapter;
